import React, { useState } from 'react';
import { motion } from 'framer-motion';

export default function QuotationModal({ isOpen, onClose, colors, spacing, shadows }) {
  const [items, setItems] = useState([]);
  const [quotationNumber, setQuotationNumber] = useState(`QTN-${Date.now()}`);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');

  // Add a new empty row
  const addRow = () => {
    setItems([...items, { name: '', mrp: 0, qty: 1, total: 0 }]);
  };

  // Update item field
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    const item = { ...updated[idx], [field]: value };
    
    // Auto-calculate total
    if (field === 'mrp' || field === 'qty') {
      const mrp = field === 'mrp' ? parseFloat(value) || 0 : item.mrp;
      const qty = field === 'qty' ? parseInt(value) || 0 : item.qty;
      item.total = Math.round(mrp * qty);
    }
    
    updated[idx] = item;
    setItems(updated);
  };

  // Remove a row
  const removeRow = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  // Calculate grand total
  const grandTotal = items.reduce((sum, item) => sum + (item.total || 0), 0);

  // Print quotation
  const printQuotation = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    const rows = items.map((item, idx) => `
      <tr${idx % 2 === 0 ? '' : ' style="background-color: #f8fafc;"'}>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0;">${idx + 1}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0;">${item.name}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; text-align: center;">${item.qty}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; text-align: right;">₹${parseFloat(item.mrp).toFixed(2)}</td>
        <td style="padding: 8px 12px; border-bottom: 1px solid #e2e8f0; text-align: right;">₹${item.total.toFixed(2)}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
      <head>
        <title>Quotation - ${quotationNumber}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #0d9488; padding-bottom: 20px; }
          .header h1 { color: #0d9488; margin: 0 0 5px 0; font-size: 24px; }
          .header p { margin: 2px 0; color: #64748b; font-size: 12px; }
          .info { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 13px; }
          .info strong { color: #334155; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background-color: #0d9488; color: white; padding: 10px 12px; text-align: left; font-size: 13px; }
          td { padding: 8px 12px; font-size: 13px; }
          .total { text-align: right; font-size: 18px; font-weight: bold; color: #0d9488; margin-top: 20px; padding-top: 10px; border-top: 2px solid #0d9488; }
          .footer { text-align: center; margin-top: 40px; font-size: 11px; color: #94a3b8; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>HCS Enterprises</h1>
          <p>12-2-460/1/1,2 Mahek's Vijaya Vihar, Gudi Malkapur Road</p>
          <p>Hyderabad, Telangana 500006</p>
          <p>GSTIN: 36DBAPS4860J2Z9</p>
        </div>
        <div class="info">
          <div>
            <strong>Quotation No:</strong> ${quotationNumber}<br>
            <strong>Date:</strong> ${new Date().toLocaleDateString('en-IN')}
          </div>
          ${customerName ? `<div><strong>Customer:</strong> ${customerName}<br>${customerPhone ? `<strong>Phone:</strong> ${customerPhone}<br>` : ''}${customerAddress ? `<strong>Address:</strong> ${customerAddress}` : ''}</div>` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Product Name</th>
              <th style="text-align: center;">Quantity</th>
              <th style="text-align: right;">MRP (₹)</th>
              <th style="text-align: right;">Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
        <div class="total">Grand Total: ₹${grandTotal.toFixed(2)}</div>
        <div class="footer">
          <p>This is a computer-generated quotation and does not require a signature.</p>
          <p>Thank you for your interest in HCS Enterprises!</p>
        </div>
        <script>
          window.onload = function() { window.print(); }
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: spacing.lg,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows['2xl'],
          padding: spacing.xl,
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: colors.text.primary, margin: 0 }}>
            📋 Create Quotation
          </h2>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.text.secondary,
              padding: spacing.sm,
            }}
          >
            ✕
          </motion.button>
        </div>

        {/* Quotation Info */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: spacing.md, marginBottom: spacing.lg, padding: spacing.lg, backgroundColor: colors.primary[50], borderRadius: '0.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: colors.text.secondary, marginBottom: spacing.xs }}>Quotation No</label>
            <input
              type="text"
              value={quotationNumber}
              onChange={(e) => setQuotationNumber(e.target.value)}
              style={{
                width: '100%',
                padding: `${spacing.xs} ${spacing.sm}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.375rem',
                fontSize: '0.9rem',
                fontWeight: 700,
                color: colors.primary[600],
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: colors.text.secondary, marginBottom: spacing.xs }}>Date</label>
            <input
              type="text"
              value={new Date().toLocaleDateString('en-IN')}
              disabled
              style={{
                width: '100%',
                padding: `${spacing.xs} ${spacing.sm}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.375rem',
                fontSize: '0.9rem',
                backgroundColor: colors.neutral[100],
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: colors.text.secondary, marginBottom: spacing.xs }}>Customer Name</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Customer name"
              style={{
                width: '100%',
                padding: `${spacing.xs} ${spacing.sm}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.375rem',
                fontSize: '0.9rem',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: colors.text.secondary, marginBottom: spacing.xs }}>Customer Phone</label>
            <input
              type="text"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="Phone number"
              style={{
                width: '100%',
                padding: `${spacing.xs} ${spacing.sm}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.375rem',
                fontSize: '0.9rem',
              }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: colors.text.secondary, marginBottom: spacing.xs }}>Customer Address</label>
            <input
              type="text"
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
              placeholder="Delivery address"
              style={{
                width: '100%',
                padding: `${spacing.xs} ${spacing.sm}`,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.375rem',
                fontSize: '0.9rem',
              }}
            />
          </div>
        </div>

        {/* Items Table */}
        <div style={{ marginBottom: spacing.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
              Items ({items.length})
            </h3>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={addRow}
              style={{
                backgroundColor: colors.secondary[500],
                color: 'white',
                padding: `${spacing.sm} ${spacing.lg}`,
                borderRadius: '0.5rem',
                border: 'none',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              + Add Row
            </motion.button>
          </div>

          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.text.secondary, border: `2px dashed ${colors.border}`, borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '1rem' }}>No items added yet. Click "Add Row" to start adding products.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: colors.secondary[500], borderBottom: `2px solid ${colors.border}` }}>
                    <th style={{ padding: spacing.md, textAlign: 'left', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>#</th>
                    <th style={{ padding: spacing.md, textAlign: 'left', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>Product Name *</th>
                    <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>MRP (₹)</th>
                    <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>Quantity</th>
                    <th style={{ padding: spacing.md, textAlign: 'right', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>Total (₹)</th>
                    <th style={{ padding: spacing.md, textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', color: 'white' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: spacing.sm, fontWeight: 600, color: colors.text.secondary, textAlign: 'center' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: spacing.sm }}>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateItem(idx, 'name', e.target.value)}
                          placeholder="Enter product name"
                          style={{
                            width: '100%',
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '0.25rem',
                            fontSize: '0.85rem',
                            minWidth: '150px',
                          }}
                        />
                      </td>
                      <td style={{ padding: spacing.sm }}>
                        <input
                          type="number"
                          value={item.mrp || ''}
                          onChange={(e) => updateItem(idx, 'mrp', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          style={{
                            width: '90px',
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '0.25rem',
                            fontSize: '0.85rem',
                            textAlign: 'center',
                          }}
                        />
                      </td>
                      <td style={{ padding: spacing.sm }}>
                        <input
                          type="number"
                          value={item.qty || ''}
                          onChange={(e) => updateItem(idx, 'qty', e.target.value)}
                          placeholder="1"
                          min="1"
                          style={{
                            width: '60px',
                            padding: `${spacing.xs} ${spacing.sm}`,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '0.25rem',
                            fontSize: '0.85rem',
                            textAlign: 'center',
                          }}
                        />
                      </td>
                      <td style={{ padding: spacing.sm, fontWeight: 700, color: colors.primary[600], textAlign: 'right', fontSize: '0.9rem' }}>
                        ₹{(item.total || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: spacing.sm, textAlign: 'center' }}>
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => removeRow(idx)}
                          style={{
                            backgroundColor: colors.error,
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            padding: `${spacing.xs} ${spacing.sm}`,
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        >
                          ✕
                        </motion.button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Grand Total */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: spacing.lg }}>
          <div style={{ width: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: spacing.md, backgroundColor: colors.primary[50], borderRadius: '0.5rem', border: `2px solid ${colors.primary[200]}` }}>
              <span style={{ fontWeight: 900, fontSize: '1.1rem', color: colors.text.primary }}>Grand Total:</span>
              <span style={{ fontSize: '1.25rem', fontWeight: 900, color: colors.primary[600] }}>
                ₹{grandTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: spacing.md }}>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={printQuotation}
            disabled={items.length === 0}
            style={{
              flex: 1,
              backgroundColor: items.length === 0 ? colors.neutral[300] : colors.primary[600],
              color: 'white',
              padding: spacing.lg,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: items.length === 0 ? 'not-allowed' : 'pointer',
              opacity: items.length === 0 ? 0.6 : 1,
              fontSize: '1rem',
            }}
          >
            🖨️ Print Quotation (₹{grandTotal.toFixed(2)})
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            style={{
              flex: 1,
              backgroundColor: colors.neutral[200],
              color: colors.text.primary,
              padding: spacing.lg,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            ❌ Close
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}